import { makeOptional } from "../../../shared/lib/helpers/schema.helpers";
import { dateRangeSchema, dateSchema, emailSchema, optionalPhoneSchema, passwordSchema, selectSchema } from "../../../shared/lib/schemas/common.schema";
import { textSchema } from "../../../shared/lib/schemas/common.schema";
import { z } from "zod";
import { otherLanguageOptions, servingPreferenceOptions } from "@/features/auth/constants/select-options";
import { ratingSchema } from "../../../shared/lib/schemas/options.schema";

/** Rating schema that accepts string or number (form inputs often return strings) and coerces to number.
 * Treats 0, empty string, null, undefined as "no selection" (optional). Valid ratings are 1-5. */
const optionalRatingSchema = z.preprocess(
    (val) => {
        if (val === null || val === undefined || val === "") return undefined;
        if (val === 0) return undefined; // 0 is used as default for new users; treat as "no selection"
        if (typeof val === "number") return val;
        if (typeof val === "string") {
            const n = parseInt(val, 10);
            return isNaN(n) || n === 0 ? undefined : n;
        }
        return undefined;
    },
    makeOptional(ratingSchema)
);

export const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
] as const;

export const tShirtSizeOptions = [
    { label: "Small", value: "small" },
    { label: "Medium", value: "medium" },
    { label: "Large", value: "large" },
    { label: "Extra Large", value: "extra_large" },
    { label: "2X Large", value: "2x_large" },
] as const;

// Schema for fields that may be null from backend - coerce to "" for initial profile load.
// superRefine enforces required when currentStep===1.
const optionalRequiredTextSchema = () =>
    z.preprocess(
        (val) => (val == null || val === "") ? "" : String(val).trim(),
        z.string().max(100)
    );

export const baseAboutMeFormSchema = z.object({
    fullname: textSchema(),
    email: emailSchema,
    /** Empty/null from API (e.g. invited volunteer) is OK; step 1 superRefine still requires phone to save. */
    phone: optionalPhoneSchema,
    organizationName: optionalRequiredTextSchema(),
    selectedRegion: makeOptional(z.union([z.number(), z.string()]).transform((val) => {
        if (!val || val === '') return undefined;
        return typeof val === 'string' ? parseInt(val, 10) : val;
    })),
    selectedRegions: makeOptional(z.union([
        z.array(z.string()),
        z.array(z.number()),
    ]).transform((val) => {
        if (!Array.isArray(val) || val.length === 0) return undefined;
        return val.map((v) => (typeof v === 'string' ? parseInt(v, 10) : v)).filter((n) => !isNaN(n));
    })),
    dateOfBirth: makeOptional(dateSchema, "date"),
    gender: makeOptional(selectSchema(genderOptions)),
    tShirtSize: makeOptional(selectSchema(tShirtSizeOptions)),
    /** Volunteers.backgroundCheck — date-only */
    backgroundCheck: makeOptional(dateSchema, "date"),
    servingPreference: makeOptional(selectSchema(servingPreferenceOptions)),
    groupNotes: makeOptional(textSchema("lg")),
    availability: makeOptional(textSchema()),
    availabilityRange: makeOptional(dateRangeSchema),
    servePeriods: makeOptional(z.array(z.object({
        from: z.date().optional(),
        to: z.date().optional(),
        regionId: z.union([z.number(), z.string()]).transform((val) => {
            if (!val || val === undefined || val === '' || val === null) return undefined;
            return typeof val === 'string' ? parseInt(val, 10) : val;
        }).optional(),
    }))),
    experienceConstruction: optionalRatingSchema,
    experienceManualLabor: optionalRatingSchema,
    experienceDataManagement: optionalRatingSchema,
    experienceStorytelling: optionalRatingSchema,
    mealPreparationAbility: optionalRatingSchema,
    experienceLeadingTeams: optionalRatingSchema,
    experienceDrywallFinishing: optionalRatingSchema,
    experienceRoughCarpentry: optionalRatingSchema,
    experienceFinishCarpentry: optionalRatingSchema,
    experienceFlooring: optionalRatingSchema,
    experienceWoodWorking: optionalRatingSchema,
    experienceElectrical: optionalRatingSchema,
    experiencePlumbing: optionalRatingSchema,
    experienceHvac: optionalRatingSchema,
    experienceArborist: optionalRatingSchema,
    experienceRoofing: optionalRatingSchema,
    experienceHeavyEquipment: optionalRatingSchema,
    experienceAltMinistries: optionalRatingSchema,
    skillsAndQualities: makeOptional(textSchema("lg")),
    otherLanguages: makeOptional(selectSchema(otherLanguageOptions)),
    prayerRequest: makeOptional(textSchema("lg")),
    vehicleMake: makeOptional(z.string().max(100)),
    vehicleModel: makeOptional(z.string().max(100)),
    vehicleColor: makeOptional(z.string().max(100)),
    vehicleLicensePlateNumber: makeOptional(z.string().max(50)),
    vehicleLicensePlateState: makeOptional(z.string().max(50)),
    passportNumber: makeOptional(z.string().max(100)),
    emergencyContact1FullName: makeOptional(z.string().max(200)),
    emergencyContact1Relationship: makeOptional(z.string().max(100)),
    emergencyContact1Phone: optionalPhoneSchema,
    emergencyContact2FullName: makeOptional(z.string().max(200)),
    emergencyContact2Relationship: makeOptional(z.string().max(100)),
    emergencyContact2Phone: optionalPhoneSchema,
    healthInsuranceProvider: makeOptional(z.string().max(200)),
    healthInsurancePhone: optionalPhoneSchema,
    healthInsurancePolicyNumber: makeOptional(z.string().max(100)),
    travelInsuranceProvider: makeOptional(z.string().max(200)),
    travelInsurancePhone: optionalPhoneSchema,
    travelInsurancePolicyNumber: makeOptional(z.string().max(100)),
    jamaicaC5ApplicationComplete: z.boolean().optional(),
    jamaicaTripFlightDetails: makeOptional(z.string().max(4000)),
    healthConditions: makeOptional(z.string().max(1000)),
    dietaryRestrictions: makeOptional(z.string().max(500)),
    hasServedBefore: makeOptional(z.enum(["yes", "no"])),
    isChristFollower: makeOptional(z.enum(["yes", "no"])),
    preferredLocation: optionalRequiredTextSchema(),
    password: makeOptional(passwordSchema),
    confirmPassword: makeOptional(passwordSchema),
    emailUpdates: makeOptional(z.boolean()),
    chatNotifications: makeOptional(z.boolean()),
})

// Function to create form schema with conditional required fields based on currentStep
export const createAboutMeFormSchema = (currentStep?: number) => {
    const baseSchema = baseAboutMeFormSchema.omit({
        password: true,
        confirmPassword: true,
    })
    
    // If currentStep = 1, add refine checks for required fields
    if (currentStep === 1) {
        return baseSchema.superRefine((data, ctx) => {
            // Required fields: Full Name, Email, Phone, Address, Organization, Date of Birth, Gender
            const requiredFields = [
                { field: 'fullname', message: 'Full name is required' },
                { field: 'email', message: 'Email is required' },
                { field: 'phone', message: 'Phone number is required' },
                { field: 'preferredLocation', message: 'Address is required' },
                { field: 'organizationName', message: 'Organization/Church name or individual is required' },
                { field: 'dateOfBirth', message: 'Date of birth is required' },
                { field: 'gender', message: 'Gender is required' },
            ]
            
            requiredFields.forEach(({ field, message }) => {
                const value = data[field as keyof typeof data]
                // Special handling for dateOfBirth (Date object)
                if (field === 'dateOfBirth') {
                    if (!value || !(value instanceof Date) || isNaN(value.getTime())) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message,
                            path: [field],
                        })
                    }
                } else if (!value || (typeof value === 'string' && value.trim() === '')) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message,
                        path: [field],
                    })
                }
            })
        })
    }
    
    return baseSchema
}

// Default form schema (for backward compatibility)
export const aboutMeFormSchema = baseAboutMeFormSchema.omit({
    password: true,
    confirmPassword: true,
})

// Schema for API updates - includes dateOfBirth for database storage
// dateOfBirth should be a string (YYYY-MM-DD format) for API submission
// servePeriods should accept Date objects (for form) or ISO date strings (for API submission)
// All rating fields (including mealPreparationAbility) use optionalRatingSchema - backend accepts number
export const updateAboutMeFormSchema = baseAboutMeFormSchema.partial().extend({
    dateOfBirth: z.union([
        z.date(),
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
        z.literal(""),
    ]).optional(),
    backgroundCheck: z.union([
        z.date(),
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
        z.literal(""),
        z.null(),
    ]).optional(),
    servePeriods: z.array(z.object({
        from: z.union([
            z.date(),
            z.string().datetime(), // Accept ISO date strings for API submission
        ]),
        to: z.union([
            z.date(),
            z.string().datetime(), // Accept ISO date strings for API submission
        ]),
        regionId: z.union([z.number(), z.string()]).optional(),
    })).optional(),
})

// Type for the form (includes dateOfBirth for UI purposes)
export type AboutMeFormType = z.infer<typeof aboutMeFormSchema>

// Type for API submission (excludes dateOfBirth)
export type UpdateAboutMeForm = z.infer<typeof updateAboutMeFormSchema>